<template>
  <div
    class="cart__product-recommendation-items pb-sm"
    v-if="loading || cartProductRecommendation?.length"
  >
    <h2 class="text--center cart__product-recommendation-title mt-0">
      You may also like
    </h2>
    <ProductListSlider
      @item-click="closeCartPopup"
      :key="cartProductRecommendation"
      :products="
        cartProductRecommendation?.length && !loading
          ? cartProductRecommendation
          : placeholderProducts
      "
      :showFirstMockupImage="showFirstMockupImage"
    />
  </div>
</template>

<script lang="ts" setup>
import { onMounted, onUnmounted, computed, ref, watch } from 'vue'
import useProductStore from '@/store/product'
import ProductListSlider from '@/components/product/ProductListSlider.vue'
import LocationService, { UserLocation } from '@/services/location'
import { Recommendation } from '@/types'
import {
  EVENT_SHOW_CART_POPUP,
  EVENT_CLOSE_CART_POPUP,
  QUERY_STRING_NO_CACHE,
  cloneDeep,
  AB_TEST_RECOMMENDATION,
  AB_TEST_VARIANT_PRODUCT_MASS,
  EVENT_TRACKING_CART_RECOMMENDATION_CLICKED,
  MIXPANEL_DESTINATION,
  destinationIntegrations,
} from '@/utils'
import EventBus from '@/services/eventbus'
import useUserStore from '@/store/user'
import { useRoute } from 'vue-router'
import useCartStore from '@/store/cart'
import { useProductRecentlyViewedItems } from '@/pages/product/composables/ProductRecentlyViewed'
import useDeviceStore from '@/store/device'
import CacheService, { getJSONfromCacheResponse } from '@/services/cache'
import Provider from '@/provider'
import { fillUpProductProperties } from '@/store/product'
import { useABTest } from '@/composables/useABTest'
import Analytics from '@/services/analytics'

const deviceStore = useDeviceStore()
const route = useRoute()
const userStore = useUserStore()
const productStore = useProductStore()
const cartStore = useCartStore()
const { getRecentlyViewedItems } = useProductRecentlyViewedItems()
const { getABTestActivedVariantByCode } = useABTest()

const loading = ref(false)
const placeholderProducts = Array(10).fill(undefined)
const productRecommendation = ref(null)
const productRecommendationShopify = ref(null)

const isCartPage = computed(() => route.name == 'cart')

const showFirstMockupImage = computed(() => {
  return deviceStore.isMobile || !isCartPage.value
})

const variantMassProductABTest = computed(() => {
  if (!productStore.isMotherOrGrandmaProduct) return null
  const abtestVariant = getABTestActivedVariantByCode(AB_TEST_RECOMMENDATION)
  if (abtestVariant === null) return null
  return abtestVariant == AB_TEST_VARIANT_PRODUCT_MASS
})

const cartProductRecommendation = computed(() => {
  const productMass = productRecommendation.value?.productMass || []
  const moreItems = productRecommendation.value?.moreItems || []
  if (
    variantMassProductABTest.value ||
    variantMassProductABTest.value === null
  ) {
    return moreItems?.length ? moreItems : productRecommendationShopify.value
  }
  return productMass?.length
    ? productMass
    : moreItems?.length
    ? moreItems
    : productRecommendationShopify.value
})

onMounted(async () => {
  EventBus.on(EVENT_SHOW_CART_POPUP, getCartRecommendation)

  if (route.path == '/cart') {
    watch(
      () => cartStore.cart?.id,
      () => {
        if (cartStore.cart?.lines?.length) {
          getCartRecommendation()
        }
      },
      {
        immediate: true,
      }
    )
  }
})

onUnmounted(() => {
  EventBus.off(EVENT_SHOW_CART_POPUP, getCartRecommendation)
})

function getCartRecommendation() {
  loading.value = true
  LocationService.onLocationReady(async (location: UserLocation | null) => {
    const countryCode = location?.country_code
    const recentlyViewedProducts = await getRecentlyViewedItems()

    let product = cartStore.cart?.lines[0]?.merchandise?.product

    if (!product) {
      product = productStore.product
    }
    if (!product && recentlyViewedProducts?.length) {
      product = recentlyViewedProducts[0]
    }
    if (!product) return (loading.value = false)

    const recomendationPayload: Recommendation = {
      recently_view: recentlyViewedProducts.map((product) => product.handle),
      product: {
        title: product.title,
        handle: product.handle,
        tags: product.tags || [],
        type: product.productType || '',
      },
      location: countryCode,
      user_segments: userStore.segments
        ? userStore.segments.map((segment) => segment.key)
        : [],
    }
    await getProductRecommendation(recomendationPayload)

    if (
      ((variantMassProductABTest.value &&
        !productRecommendation.value?.productMass?.length) ||
        (!variantMassProductABTest.value &&
          !productRecommendation.value?.moreItems?.length)) &&
      product.id
    ) {
      const isForceLoadNewData = !!route.query[QUERY_STRING_NO_CACHE]

      await getProductRecommendationsShopify({
        id: product.id,
        isForceLoadNewData,
      })
    }
    loading.value = false
  })
}

async function getProductRecommendation(payload: Recommendation) {
  try {
    const cacheKey = `cart_getProductRecommendation_${JSON.stringify(payload)}`
    let products

    const cacheResponse = await CacheService.instance?.get(cacheKey)
    const productRecommendationFromCache =
      getJSONfromCacheResponse(cacheResponse)

    if (productRecommendationFromCache) {
      products = productRecommendationFromCache
    }

    if (!products) {
      const provider = await Provider.getInstance()
      products = await provider.getProductRecommendation(payload)

      if (products) {
        CacheService.instance?.set(cacheKey, products)
      } else {
        CacheService.instance?.delete(cacheKey)
      }
    }

    productRecommendation.value = {
      moreItems: products?.moreItems.map(
        (product) => fillUpProductProperties(product)!
      ),
      productMass: products?.productMass.map(
        (product) => fillUpProductProperties(product)!
      ),
    }
    return productRecommendation.value
  } catch (error: any) {}
}

async function getProductRecommendationsShopify({
  id,
  isForceLoadNewData,
}: {
  id: string
  isForceLoadNewData?: boolean
}) {
  try {
    const cacheKey = `cart_getProductRecommendationsShopify_${id}`
    let products = null
    if (!isForceLoadNewData) {
      const cacheResponse = await CacheService.instance?.get(cacheKey)
      const productRecommendationFromCache =
        getJSONfromCacheResponse(cacheResponse)

      if (productRecommendationFromCache) {
        products = productRecommendationFromCache

        productRecommendationShopify.value = products?.map((product) =>
          fillUpProductProperties(product)
        )
        return productRecommendationShopify.value
      }
    }

    const provider = await Provider.getInstance()
    products = await provider.getProductRecommendations(id)

    if (products?.length) {
      products = products.map((product) => ({
        ...product,
        images: cloneDeep(product.images).reverse(),
      }))

      CacheService.instance?.set(cacheKey, products)
    }

    productRecommendationShopify.value = products?.map((product) =>
      fillUpProductProperties(product)
    )
    return productRecommendationShopify.value
  } catch (error: any) {}
}

function closeCartPopup() {
  EventBus.trigger(EVENT_CLOSE_CART_POPUP)
  trackingCartRecommendationClicked()
}

function trackingCartRecommendationClicked() {
  Analytics.track(
    EVENT_TRACKING_CART_RECOMMENDATION_CLICKED
  )
}
</script>

<style lang="scss">
.cart__product-recommendation-items {
  overflow: hidden;
  border-radius: 5px;
  border: solid 1px var(--border-color-box);
  overflow: hidden;

  grid-area: c;
  .product-list-slider {
    margin: 8px;
  }
  .cart__product-recommendation-title {
    font-size: 15px;
    font-weight: 500;
    opacity: 0.85;
    align-items: center;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 45px;
    background: var(--background-color);
  }
  .product-block__title a {
    -webkit-line-clamp: 2;
    font-size: 13px;
    opacity: 0.85;
    color: var(--ink);
    transition: all var(--animation-duration);
  }
  .product-block:hover {
    .product-block__title a {
      color: var(--color-primary);
    }
  }
  .product-block__type {
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  .product-block__discount-badge {
    display: none;
  }
  @include media-lg-down {
    .product-block__image.image-container {
      padding-top: 0;
    }
    .product-block__image.image-container,
    .product-block .image-container,
    .product-block__image--rectangular img,
    .product-block .image-rectangular {
      aspect-ratio: 1;
      height: 120px !important;
    }
    .product-block__personalize-badge {
      display: none;
    }
    .product {
      &-block {
        gap: 1em;
        display: flex;
        flex-direction: row;
        &__image,
        &__image a {
          width: 89px;
        }
        &__info {
          gap: 1em;
        }
        &__price {
          font-weight: 400;
          font-size: 14px;
        }
        &__price-final {
          color: var(--color-secondary);
        }
        &__title {
          margin: 0;
        }
        &__type-and-price {
          margin: 0;
        }
      }
    }
    .carousel {
      &__button.is-prev,
      &__button.is-next {
        background: #ffffff;
        color: var(--text-color-primary);
        bottom: 0 !important;
        top: unset;
        position: absolute;
        transform: translateY(35px);
        z-index: 1;
        svg {
          width: 1rem;
        }
      }
      &__button.is-prev {
        left: 50px;
      }
      &__button.is-next {
        right: 50px;
      }
      &__dots {
        margin-top: 4px;
      }
      --carousel-slide-width: calc((100% - 1em) - 1px);
    }
  }
}

.cart-popup {
  .cart__product-recommendation-items {
    .product-block__image.image-container {
      padding-top: 0;
    }
    .product-block__image.image-container,
    .product-block .image-container,
    .product-block__image--rectangular img,
    .product-block .image-rectangular {
      aspect-ratio: 1;
      height: 120px !important;
    }
    .product-block__personalize-badge {
      display: none;
    }
    .product-block .mkc-detail {
      height: 0;
      aspect-ratio: 1;
    }
    .carousel__slide {
      height: 120px;
    }
    .product {
      &-block {
        gap: 1em;
        display: flex;
        flex-direction: row;
        &__image,
        &__image a {
          width: 89px;
        }
        &__info {
          gap: 1em;
        }
        &__price {
          font-weight: 400;
          font-size: 14px;
        }
        &__price-final {
          color: var(--color-secondary);
        }
        &__title {
          margin: 0;
        }
        &__type-and-price {
          margin: 0;
        }
      }
    }
    .carousel {
      &__button.is-prev,
      &__button.is-next {
        background: #ffffff;
        color: var(--text-color-primary);
        bottom: 0 !important;
        top: unset;
        position: absolute;
        transform: translateY(35px);
        z-index: 1;
        svg {
          width: 1rem;
        }
      }
      &__button.is-prev {
        left: 50px;
      }
      &__button.is-next {
        right: 50px;
      }
      &__dots {
        margin-top: 4px;
      }
      --carousel-slide-width: calc((100% - 1em) - 1px);
    }
  }
}
.product-block__type {
  color: var(--accent);
}
</style>
